import classNames from "classnames";
import { Tooltip } from "react-tooltip";
import { Fragment, ReactNode } from "react";
import ReactDOM from "react-dom";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useSmartPosition } from "../../../shared/hooks/useSmartPosition";

export default function PopoverPanel({
  icon,
  styles,
  children,
  buttonText,
  onClick,
  tooltipContent,
  closeAction = true,
  showArrow = true
}: {
  icon?: ReactNode;
  buttonText?: string;
  children: ReactNode;
  showArrow?: boolean;
  closeAction?: boolean;
  onClick?: () => void;
  tooltipContent?: string;
  styles?: {
    button?: string;
    arrow?: string;
    panel?: string;
    buttonText?: string;
    container?: string;
    childContainer?: string;
  };
}) {
  const { elementRef, position, calculatePosition } = useSmartPosition({
    width: 180,
    height: 120,
    offset: 8,
    preferredPosition: "bottom-left"
  });

  return (
    <div className={classNames("w-full", styles?.container || "")}>
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button
              ref={elementRef as any}
              onClick={() => {
                onClick?.();
                calculatePosition();
              }}
              data-tooltip-content={tooltipContent ?? ""}
              data-tooltip-id={tooltipContent ? "popoverButton" : undefined}
              className={classNames(
                "group flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-opacity-75",
                open ? "" : "text-opacity-90",
                styles?.button || ""
              )}
            >
              {icon ? (
                icon
              ) : (
                <>
                  <span className={styles?.buttonText}>{buttonText}</span>
                  {!!showArrow && (
                    <ChevronDownIcon
                      aria-hidden="true"
                      className={classNames(
                        "ml-2 h-5 w-5 text-indigo-100 transition duration-150 ease-in-out group-hover:text-opacity-80",
                        open ? "" : "text-opacity-70",
                        styles?.arrow || ""
                      )}
                    />
                  )}
                </>
              )}
            </Popover.Button>

            {open &&
              ReactDOM.createPortal(
                <Transition
                  as={Fragment}
                  show={open}
                  leaveTo="opacity-0 translate-y-1"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leaveFrom="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  enter="transition ease-out duration-200"
                >
                  <Popover.Panel
                    static
                    className="fixed z-[9999] bg-white border rounded-xl shadow-lg p-1 min-w-[180px]"
                    style={{
                      top: `${position.top}px`,
                      left: `${position.left}px`
                    }}
                  >
                    {({ close }) => (
                      <div
                        className={classNames(
                          "overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5",
                          styles?.childContainer || ""
                        )}
                        onClick={() => {
                          if (closeAction) {
                            close();
                          }
                        }}
                      >
                        {children}
                      </div>
                    )}
                  </Popover.Panel>
                </Transition>,
                document.body
              )}
          </>
        )}
      </Popover>
      {tooltipContent && (
        <Tooltip
          id="popoverButton"
          className="!bg-[#212121] !text-xs !font-semibold !rounded-lg"
        />
      )}
    </div>
  );
}
